import React, { useMemo, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import block from 'bem-cn';
import SVGInline from 'react-svg-inline';

import { actions } from 'features/Game';
import Button from 'components/Button';
import Input from 'components/Input';
import { lines, players, getWinnings } from 'features/Game/data';
import { roundingFunc } from 'services/helpers/roundingFunc';

import autoPick from './img/auto-pick.svg';
import playerSvg from './img/player.svg';
import minusSvg from './img/minus.svg';
import plusSvg from './img/plus.svg';

import './Basket.scss';

const b = block('basket');

const Basket = () => {
  const dispatch = useDispatch();
  const { linesCount, playersCount, amount, isAutoPick } = useSelector(state => state.game.gameSettings);
  const locale = useSelector(state => state.locale.locale);
  const limits = useSelector(state => state.game.limits);
  const balance = useSelector(state => state.auth.balance);
  const gameStatus = useSelector(state => state.game.gameStatus);
  const temp_pick = useSelector(state => state.game.temp_pick);
  const rtp = useSelector(state => state.game.rtp);
  const coeffs = useSelector(state => state.game.coeffs);

  const setAmount = useCallback(amount => dispatch(actions.setGameSettings({ amount })), [dispatch])
  const setIsAutoPick = useCallback(isAutoPick => dispatch(actions.setGameSettings({ isAutoPick })), [dispatch])
  const setLinesCount = useCallback(linesCount => dispatch(actions.setGameSettings({ linesCount })), [dispatch])
  const setPlayersCount = useCallback(playersCount => dispatch(actions.setGameSettings({ playersCount })), [dispatch])

  // Set min amount
  useEffect(() => {
    setAmount(limits.min)
  }, [limits.min, setAmount])

  const isPlaced = useMemo(() => gameStatus === 1, [gameStatus]);

  const switchAutoPick = useCallback(() => {
    if (!isPlaced && (playersCount === 1)) {
      setIsAutoPick(!isAutoPick)
    }
  }, [isAutoPick, isPlaced, playersCount, setIsAutoPick]);

  const placeBet = useCallback(() => {
    dispatch(actions.startGame())
  }, [dispatch])

  const completeGame = useCallback(() => {
    dispatch(actions.completeGame())
  }, [dispatch])

  // Possible win
  const possibleAmount = useMemo(() => getWinnings(linesCount, playersCount, rtp, amount, coeffs)[temp_pick.length - 1] || getWinnings(linesCount, playersCount, rtp, amount, coeffs)[0], [amount, coeffs, linesCount, playersCount, rtp, temp_pick.length])

  // Button text
  const buttonText = useMemo(() => isPlaced ? `${locale.cashout} (${roundingFunc(+possibleAmount)})` : locale.startGame, [isPlaced, locale.cashout, locale.startGame, possibleAmount]);

  // Function of cick
  const buttonFunc = useMemo(() => isPlaced ? completeGame : placeBet, [completeGame, isPlaced, placeBet]);

  // Auto Bet
  const isEnableAutoPick = useMemo(() => playersCount === 1, [playersCount])

  useEffect(() => {
    if (playersCount !== 1) setIsAutoPick(false);
  }, [playersCount, setIsAutoPick])

  // Lines
  const linesList = useMemo(() => lines.map(t => <div className={b('line-item', { isActive: t === linesCount })} key={t} onClick={() => setLinesCount(t)}>
    {t}
  </div>), [linesCount, setLinesCount])

  //Players
  const getPlayers = count => {
    let result = [];
    for (let i = 0; i < count; i++) {
      result = [...result, <SVGInline key={i} svg={playerSvg} className={b('player').toString()} />]
    }
    return result;
  }

  const playersList = useMemo(() => players.map(t => <div className={b('player-item', { isActive: t === playersCount })} key={t} onClick={() => setPlayersCount(t)}>
    {getPlayers(t)}
  </div>), [playersCount, setPlayersCount])

  // Is minimal amount
  const isMinimalAmount = useMemo(() => limits.min === amount, [amount, limits.min])

  // For validate amount
  const validateChangeAmount = useCallback(value => {
    if (!isPlaced) {
      if (value > limits.max) setAmount(+limits.max.toFixed(1))
      else if (value < limits.min) setAmount(+limits.min.toFixed(1))
      else if (+(+value).toFixed(1) > balance) {
        if (balance < limits.min) {
          setAmount(+limits.min.toFixed(1))
        } else {
          setAmount(Math.floor(balance * 10, 1) / 10)
        }
      }
      else setAmount(+(+value).toFixed(1));
    }
  }, [balance, isPlaced, limits.max, limits.min, setAmount])

  // For validate input Amount
  const validateTempAmount = useCallback(value => {
    if (!isPlaced) {
      if ((!isNaN(+value) || value.toString().indexOf('.') === value.toString().length - 1) &&
        value.toString().split('').filter(t => t === '.').length < 2) {
        if (value.toString().split('')[0] === '.') {
          setAmount(value.substring(1, value.length))
        } else setAmount(value)
      }
    }
  }, [isPlaced, setAmount])

  return <footer className={b('footer', { isPlaced })}>
    <div className={b('change-container')}>
      <div className={b('change-block', { isMinimalAmount })} onClick={() => validateChangeAmount(amount - 10 * limits.min)}>
        <SVGInline svg={minusSvg} className={b('change-icon').toString()} />
      </div>
      <div className={b('change-block')}>
        <Input
          value={amount}
          onChange={e => validateTempAmount(e.currentTarget.value)}
          onBlur={() => validateChangeAmount(amount)}
          unBorder
          centerText />
      </div>
      <div className={b('change-block')} onClick={() => validateChangeAmount(amount + 10 * limits.min)}>
        <SVGInline svg={plusSvg} className={b('change-icon').toString()} />
      </div>
    </div>
    <div className={b('bets')}>
      <div className={b('bet-block')} onClick={() => validateChangeAmount(limits.min)}>
        {locale.min}
      </div>
      <div className={b('bet-block')} onClick={() => validateChangeAmount(amount * 2)}>
        x2
      </div>
      <div className={b('bet-block')} onClick={() => validateChangeAmount(balance)}>
        {locale.max}
      </div>
    </div>
    <div className={b('players')}>
      {playersList}
    </div>
    <div className={b('lines')}>
      {linesList}
    </div>
    <div className={b('auto-pick', { isActive: isAutoPick, isEnableAutoPick })} onClick={switchAutoPick}>
      <SVGInline svg={autoPick} className={b('pick-image').toString()} />
      {locale.autoPick}
    </div>
    <div className={b('main-button')}>
      <Button
        text={buttonText}
        isDisabled={(temp_pick.length === 0) && isPlaced}
        size='container'
        isUppercase
        color='orange'
        callBack={buttonFunc}
        fontSize='normal' />
    </div>
  </footer>
}

export default Basket;